import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";

const blue = "#00acdc";
const green = "#94ba33";
const amber = "rgba(251,191,36)";
const red = "#d71433";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const currentUtcMonth = () => {
  const n = new Date();
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}`;
};
const fmtDay = (iso) => {
  if (!iso) {return iso;}
  const d = new Date(String(iso).split("T")[0].split(" ")[0] + "T00:00:00Z");
  if (isNaN(d)) {return iso;}
  return `${d.getUTCDate()} ${d.toLocaleString("en", { month: "short", timeZone: "UTC" })}`;
};

// SchedulerUtilisationDetailModal — drill-down for a single scheduler row.
// Per-day scheduled, weekday + hour-of-day distribution, area split, and phone by hour.
const SchedulerUtilisationDetailModal = ({ open, onClose, userId, days, month, utilisation }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !userId) {return;}
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    apiReports.getSchedulerUtilisationDetail(userId, days, month)
      .then((res) => {
        if (cancelled) {return;}
        const r = res?.data?.result;
        if (res?.data?.status === 200 && r) {
          setDetail(r);
        } else {
          setError(res?.data?.message || "Failed to load detail.");
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError("Failed to load detail."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, userId, days, month]);

  const daily = detail?.daily || [];
  const dailyData = {
    labels: daily.map((d) => fmtDay(d.day)),
    datasets: [
      { label: "Scheduled", data: daily.map((d) => d.scheduled), backgroundColor: green, maxBarThickness: 18 },
    ],
  };
  const dailyOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Policies" } }],
      xAxes: [{ ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, maxTicksLimit: 10, autoSkipPadding: 4 } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  const weekday = detail?.weekday || [];
  const weekdayData = {
    labels: weekday.map((w) => w.name),
    datasets: [{ label: "Made", data: weekday.map((w) => w.made), backgroundColor: blue }],
  };
  const weekdayOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
    tooltips: { mode: "index", intersect: false },
  };

  const hourOfDay = detail?.hourOfDay || [];
  const hourData = {
    labels: hourOfDay.map((h) => `${h.hour}:00`),
    datasets: [{ label: "Made", data: hourOfDay.map((h) => h.made), backgroundColor: amber, maxBarThickness: 14 }],
  };
  const hourOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 24, callback: (v) => `${v}:00` }, scaleLabel: { display: true, labelString: "Hour (US/Eastern)" } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  const phoneHours = detail?.phoneHours || [];
  const phoneHourData = {
    labels: phoneHours.map((h) => `${h.hour}:00`),
    datasets: [{ label: "Phone", data: phoneHours.map((h) => h.made), backgroundColor: blue, maxBarThickness: 14 }],
  };
  const phoneHourOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Outgoing" } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 24, callback: (v) => `${v}:00` }, scaleLabel: { display: true, labelString: "Hour (ET)" } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  const maxSplit = Math.max(1, ...(detail?.areaSplit || []).map((a) => a.scheduled));

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{
        top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        position: "absolute", maxWidth: 920, width: "92%",
        maxHeight: "88vh", overflowY: "auto",
      }} className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 tw-outline-none">
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
          <div>
            <h2 className="tw-text-2xl tw-font-semibold">{detail?.name || "Scheduler"}</h2>
            {detail &&
              <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
                {month ? `Calendar month ${month}${month === currentUtcMonth() ? " (to date)" : ""}` : `Last ${detail.windowDays} days`}
              </div>}
          </div>
          <button onClick={onClose} className="tw-text-xl tw-px-2">✕</button>
        </div>

        {loading && <div className="tw-flex tw-justify-center tw-py-10"><CircularProgress /></div>}
        {error && <div className="tw-text-fs-red tw-py-6 tw-text-center">{error}</div>}

        {detail &&
          <>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-mb-6">
              <Stat label="Scheduled" value={detail.scheduled} color="tw-bg-fs-green" />
              <Stat label="In-app hours" value={detail.pageHours} subtitle="active: visible + focused" color="tw-bg-primary" />
              <Stat label="Engaged hours" value={detail.engagedHours} subtitle="in-app + call talk time" color="tw-bg-fs-dark-blue" />
              <Stat label="Report edits" value={detail.reportEdits} subtitle="report saves + QA edits" color="tw-bg-fs-green" />
              <Stat label="Dwell hours" value={detail.dwellHours} subtitle={`${detail.activeDays}d · inspection page time (incl. idle)`} color="tw-bg-yellow-500" />
              <Stat label="Utilised" value={pct(utilisation)} subtitle="vs best performer" color="tw-bg-fs-dark-blue" />
            </div>

            <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-mb-6">
              <h3 className="tw-font-semibold tw-mb-2">System activity by day <span className="tw-text-xs tw-font-normal tw-text-gray-500">(first to last event ET · in-app · calls · engaged)</span></h3>
              {(detail.days || []).length === 0
                ? <p className="tw-text-gray-500 tw-text-sm">No system activity in this window.</p>
                : <div className="tw-overflow-x-auto" style={{ maxHeight: 260, overflowY: "auto" }}>
                  <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                    <thead>
                      <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                        <th className="tw-px-3 tw-py-1 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Day</th>
                        <th className="tw-px-3 tw-py-1 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">First</th>
                        <th className="tw-px-3 tw-py-1 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Last</th>
                        <th className="tw-px-3 tw-py-1 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Presence h</th>
                        <th className="tw-px-3 tw-py-1 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">In-app h</th>
                        <th className="tw-px-3 tw-py-1 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Call min</th>
                        <th className="tw-px-3 tw-py-1 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Engaged h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.days || []).map((d, i) =>
                        <tr key={d.day} className={i % 2 ? "tw-bg-white" : ""}>
                          <td className="tw-px-3 tw-py-1 tw-text-sm">{fmtDay(d.day)}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-font-mono">{d.first}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-font-mono">{d.last}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-text-right">{d.presenceHours}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-text-right">{d.pageHours}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-text-right">{d.callMinutes}</td>
                          <td className="tw-px-3 tw-py-1 tw-text-sm tw-text-right tw-font-semibold">{d.engagedHours}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>}
            </div>

            <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-mb-6">
              <h3 className="tw-font-semibold tw-mb-2">Policies scheduled per day</h3>
              <div style={{ height: 220 }}>
                {daily.some((d) => d.scheduled > 0)
                  ? <Bar data={dailyData} options={dailyOpts} />
                  : <div className="tw-text-gray-500 tw-text-sm">No appointments in this window.</div>}
              </div>
            </div>

            <div className="tw-grid md:tw-grid-cols-2 tw-gap-6 tw-mb-6">
              <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                <h3 className="tw-font-semibold tw-mb-2">Activity by weekday</h3>
                <div style={{ height: 200 }}><Bar data={weekdayData} options={weekdayOpts} /></div>
              </div>
              <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                <h3 className="tw-font-semibold tw-mb-2">Activity by hour <span className="tw-text-xs tw-font-normal tw-text-gray-500">(ET)</span></h3>
                <div style={{ height: 200 }}><Bar data={hourData} options={hourOpts} /></div>
              </div>
            </div>

            <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
              <h3 className="tw-font-semibold tw-mb-3">Scheduled by area</h3>
              {(detail?.areaSplit || []).length === 0
                ? <p className="tw-text-gray-500 tw-text-sm">No policies scheduled in this window.</p>
                : <div className="tw-space-y-2">
                  {detail.areaSplit.map((a) =>
                    <div key={a.workAreaId}>
                      <div className="tw-flex tw-justify-between tw-text-sm tw-mb-0.5">
                        <span className="tw-truncate">{a.name}</span>
                        <span className="tw-font-medium">{a.scheduled} <span className="tw-text-gray-400">({pct(a.pct)})</span></span>
                      </div>
                      <div className="tw-h-2 tw-rounded tw-bg-gray-200 tw-overflow-hidden">
                        <div className="tw-h-full" style={{ width: `${a.scheduled / maxSplit * 100}%`, background: green }} />
                      </div>
                    </div>
                  )}
                </div>}
            </div>

            <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-mt-6">
              <h3 className="tw-font-semibold tw-mb-2">Phone activity <span className="tw-text-xs tw-font-normal tw-text-gray-500">(outgoing)</span></h3>
              <div className="tw-flex tw-gap-6 tw-mb-3 tw-text-sm">
                <span><b className="tw-text-lg">{detail?.texts || 0}</b> texts</span>
                <span><b className="tw-text-lg">{detail?.calls || 0}</b> calls</span>
                <span><b className="tw-text-lg">{detail?.missed || 0}</b> missed</span>
                <span><b className="tw-text-lg">{detail?.callHours || 0}</b> hrs talk time</span>
              </div>
              <div style={{ height: 200 }}><Bar data={phoneHourData} options={phoneHourOpts} /></div>
            </div>
          </>
        }
      </div>
    </Modal>
  );
};

const Stat = ({ label, value, subtitle, color }) =>
  <div className={`tw-rounded-lg tw-p-3 tw-text-white ${color}`}>
    <div className="tw-text-2xl tw-font-bold">{value}</div>
    <div className="tw-text-xs tw-uppercase tw-tracking-wider tw-opacity-90">{label}</div>
    {subtitle && <div className="tw-text-xs tw-opacity-80 tw-mt-0.5">{subtitle}</div>}
  </div>
;

export default SchedulerUtilisationDetailModal;